import { useState, type ReactNode } from 'react';
import { IconActivity, IconBell, IconBrush, IconCalendarEvent, IconDeviceDesktop, IconKeyboard, IconLock, IconMessageCircle, IconPlus, IconPrinter, IconShield, IconSparkles, IconTool, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Modal, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useWide } from '../sales/useWide';
import { BACKUPS, CHANNELS, DELEGS0, DESK_CARDS, DEVICES, DOCK0, HANA_PLUS, KEYS, MAILBOXES, NOTI, PASSKEYS, SECS, SEC_N, SEATS, SESSIONS, LANG_LABEL, PEOPLE, USER, moveItem, notiOn, seatLine, type Deleg, type SecKey } from './settings';
import { useT } from '@hana/i18n';
import './i18n';
import s from './settings.module.css';

// Mint-Settings (1536): Cài đặt cá nhân · 10 mục. Dữ liệu mẫu; nút ghi chỉ đổi trạng thái trên máy + toast (chưa ghi DB).

const SEC_ICON: Record<SecKey, typeof IconUser> = { profile: IconUser, look: IconBrush, ask: IconSparkles, security: IconShield, desk: IconDeviceDesktop, tools: IconTool, noti: IconBell, backup: IconUsersGroup, sessions: IconDeviceDesktop, keys: IconKeyboard };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

const ACT_LOGIN: readonly (readonly [string, string, string])[] = [['Đăng nhập Windows · Chrome · 235 Cộng Hòa', 'Hôm nay 08:02', 'var(--d-green)'], ['Đổi backup ghế SALE-MGR → TUANNT', 'Hôm qua 17:40', 'var(--d-blue)'], ['Đăng nhập iPhone 15 · HANA Ezy', 'Hôm qua 07:55', 'var(--d-green)'], ['Đăng nhập thất bại 1 lần · sai mã', '20/Oct/2026 21:12', 'var(--d-amber)'], ['Bật chế độ Gọn', '18/Oct/2026 10:05', 'var(--d-blue)'], ['Đăng xuất từ xa iPad cũ', '15/Oct/2026 09:30', 'var(--d-gray)']];
const ACT_CHANGE: readonly (readonly [string, string, string])[] = [['Tạo uỷ quyền BOD-DDIR → PHILIP (24–25/Oct)', 'Hôm qua 16:20', 'var(--d-violet)'], ['Duyệt 3 thay đổi tham số của KTT', '21/Oct/2026 11:02', 'var(--d-blue)'], ['Tự duyệt release B/L SE260639118 (cờ)', '20/Oct/2026 15:44', 'var(--d-amber)'], ['Thêm app_param.debit_remind_days = 2 (bỏ tự xác nhận debit)', '19/Oct/2026 09:12', 'var(--d-blue)']];

/** Cột phải Hoạt động tài khoản (Mint-Settings: tab Đăng nhập · Thay đổi). */
function ActPanel({ tab, onTab }: { tab: 'login' | 'change'; onTab: (k: 'login' | 'change') => void }) {
  const rows = tab === 'login' ? ACT_LOGIN : ACT_CHANGE;
  return (
    <div className={s.act}>
      <Segmented label="Cột phải" value={tab} onChange={onTab} items={[{ key: 'login', label: 'Đăng nhập' }, { key: 'change', label: 'Thay đổi' }]} />
      {rows.map(([k, w, c]) => <div key={k} className={s.actI}><span aria-hidden="true" className={s.dot} style={{ background: c }} /><div><div className={s.ik}>{k}</div><div className={s.rd}>{w}</div></div></div>)}
    </div>
  );
}

function Switch({ on, label, onChange }: { on: boolean; label: string; onChange: () => void }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} className={`${s.sw} ${on ? s.swOn : ''}`} onClick={onChange}><span /></button>;
}
function Seg<K extends string>({ label, value, items, onChange }: { label: string; value: K; items: readonly (readonly [K, string])[]; onChange: (k: K) => void }) {
  return (
    <div className={s.segG} role="radiogroup" aria-label={label}>
      {items.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={k === value} className={s.segB} onClick={() => onChange(k)}>{l}</button>)}
    </div>
  );
}
function Row({ k, d, warn, children }: { k: string; d?: string; warn?: boolean; children?: ReactNode }) {
  return (
    <div className={s.row}>
      <div className={s.rl}><div className={s.rk}>{k}</div>{d && <div className={`${s.rd} ${warn ? s.warn : ''}`}>{d}</div>}</div>
      {children && <div className={s.rc}>{children}</div>}
    </div>
  );
}
function Items({ children }: { children: ReactNode }) { return <div className={s.items}>{children}</div>; }
function Item({ k, sub, chip, tone, dim, children }: { k: string; sub?: string; chip?: string; tone?: 'pri' | 'warn' | 'ok' | 'mute'; dim?: boolean; children?: ReactNode }) {
  return (
    <div className={s.item} style={dim ? { opacity: 0.6 } : undefined}>
      <div className={s.il}><div className={s.ik}>{k}</div>{sub && <div className={s.rd}>{sub}</div>}</div>
      {chip && <span className={`${s.chip} ${s[tone ?? 'mute']}`}>{chip}</span>}
      {children}
    </div>
  );
}
function Card({ id, title, label, meta, tight, icon, children }: { id: string; title?: string; label?: string; meta?: string; tight?: boolean; icon?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} aria-label={label ?? title} className={`${s.card} ${tight ? s.tight : ''}`}>
      {title && <div className={s.cHead}>{icon}<b>{title}</b>{meta && <span className={s.rd}>{meta}</span>}</div>}
      {children}
    </section>
  );
}
const SB = ({ label, onClick, children, soft }: { label?: string; onClick: () => void; children: ReactNode; soft?: boolean }) => (
  <Button size="sm" variant={soft ? 'soft' : 'secondary'} aria-label={label} onClick={onClick}>{children}</Button>
);

export function Settings() {
  const t = useT();
  const toast = useToast();
  const wide = useWide();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const [cur, setCur] = useState<SecKey>('profile');
  const [right, setRight] = useState(false);
  const [rTab, setRTab] = useState<'login' | 'change'>('login');
  const [theme, setTheme] = useState('light');
  const [dense, setDense] = useState('std');
  const [lang, setLang] = useState('vi');
  const [seat, setSeat] = useState('SALE-MGR');
  const [sw, setSw] = useState<Record<string, boolean>>({ rm: false, bub: true, dnd: false, side: false, pin: false, wideTl: true, h3: true, h4: true, h6: true, h7: true, h10: false, dockPin: false, files: true });
  const [sg, setSg] = useState<Record<string, string>>({ sug: 'smart', lock: '15', desk: 'role', dock: 'right', num: 'vi', jr: 'mine' });
  const [noti, setNoti] = useState<Record<string, boolean>>({});
  const [plusReq, setPlusReq] = useState('');
  const [pkDel, setPkDel] = useState<number[]>([]);
  const [devOff, setDevOff] = useState<number[]>([]);
  const [devTest, setDevTest] = useState<number[]>([]);
  const [mb, setMb] = useState<Record<string, boolean>>({ 'info@': true, 'support@': true, 'trf@': false });
  const [hide, setHide] = useState<string[]>([]);
  const [dock, setDock] = useState<string[]>([...DOCK0]);
  const [bk, setBk] = useState<Record<string, string>>({});
  const [bkOpen, setBkOpen] = useState<string | null>(null);
  const [dels, setDels] = useState<readonly Deleg[]>(state === 'empty' ? [] : DELEGS0);
  const [out, setOut] = useState<string | null>(null);
  const [outAll, setOutAll] = useState(false);
  const [gone, setGone] = useState<string[]>([]);
  const [mDel, setMDel] = useState(false);
  const [dSeat, setDSeat] = useState('SALE-MGR');
  const [dTo, setDTo] = useState('');
  const [dTried, setDTried] = useState(false);

  const say = (msg: string) => toast({ msg });
  const tog = (k: string, label: string) => () => { setSw((o) => ({ ...o, [k]: !o[k] })); say(`${sw[k] ? 'Đã tắt' : 'Đã bật'} ${label}`); };
  const pick = (k: string, label: string) => (v: string) => { setSg((o) => ({ ...o, [k]: v })); say(`${label}: ${v}`); };
  const go = (id: SecKey) => {
    setCur(id);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(id)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  };
  const closeDel = () => { setMDel(false); setDTried(false); setDTo(''); };
  const saveDel = () => {
    if (!dTo) { setDTried(true); return; }
    setDels((l) => [{ id: Date.now(), dir: 'Tôi giao', k: `${dSeat} → ${dTo}`, when: '23/Oct/2026 08:00 → 24/Oct/2026 12:00 · uỷ quyền tạm' }, ...l]);
    say(`Đã uỷ quyền ${dSeat} cho ${dTo}`);
    closeDel();
  };
  const sessions = SESSIONS.filter((x) => !gone.includes(x.k));

  const ready = (
    <>
      <Card id="profile" label="Hồ sơ">
        <div className={s.me}>
          <span aria-hidden="true" className={s.av}>DV</span>
          <div className={s.meT}><div className={s.name}>{USER.name} · {USER.code}</div><div className={s.rd}>{t('sys.acct', { mail: USER.mail })}</div></div>
          <a className={s.lnk} href="/mee">{t('sys.openMee')}</a>
          <Button onClick={() => say('Đổi ảnh chưa mở ở bản này')}>{t('sys.changePhoto')}</Button>
        </div>
        <div>
          <b className={s.sub}>{t('sys.seatsHeld')}</b>
          <div className={s.items}>
            {SEATS.map((x) => (
              <div key={x.code} className={s.seat}>
                <b className={s.rk}>{x.code}</b><span>{x.name}</span>
                <span className={`${s.chip} ${s[x.tone]}`}>{x.type}</span>
                <span className={s.since}>{x.since && <><IconCalendarEvent size={12} aria-hidden="true" />{x.since}</>}{!x.since && 'không theo ghế'}</span>
                {x.code === seat && <span className={`${s.chip} ${s.ok} ${s.end}`}>{t('sys.working')}</span>}
                {x.code !== seat && x.code !== 'ADMIN' && <span className={s.end}><Button size="sm" onClick={() => { setSeat(x.code); say(`Đang làm việc với ghế ${x.code} · Tasklist, hộp duyệt đổi theo`); }}>{t('sys.workSeat')}</Button></span>}
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card id="look" title="Giao diện" tight>
        <Row k="Chế độ màu"><Seg label="Chế độ màu" value={theme} onChange={(v) => { setTheme(v); say(v === 'auto' ? 'Theo hệ điều hành · máy này đang sáng' : `Chế độ màu: ${v === 'dark' ? 'Tối' : 'Sáng'}`); }} items={[['light', 'Sáng'], ['dark', 'Tối'], ['auto', 'Tự động']]} /></Row>
        <Row k="Mật độ"><Seg label="Mật độ" value={dense} onChange={(v) => { setDense(v); say(`Mật độ: ${v === 'std' ? 'Chuẩn' : 'Gọn'}`); }} items={[['std', 'Chuẩn'], ['tight', 'Gọn']]} /></Row>
        <Row k="Ngôn ngữ"><select className={s.sel} value={lang} onChange={(e) => { setLang(e.target.value); say('Ngôn ngữ đổi sau khi tải lại'); }}><option value="vi">{LANG_LABEL.vi}</option><option value="en">{LANG_LABEL.en}</option></select></Row>
        <Row k="Giảm chuyển động"><Switch on={!!sw.rm} label="Giảm chuyển động" onChange={tog('rm', 'giảm chuyển động')} /></Row>
      </Card>

      <Card id="hei" title="Bong bóng Hei" tight icon={<IconMessageCircle size={22} stroke={1.9} className={s.hei} aria-hidden="true" />}>
        <Row k="Hiện bong bóng"><Switch on={!!sw.bub} label="Hiện bong bóng Hei" onChange={tog('bub', 'bong bóng Hei')} /></Row>
        <Row k="Không làm phiền"><Switch on={!!sw.dnd} label="Không làm phiền" onChange={tog('dnd', 'không làm phiền')} /></Row>
        <Row k="Vị trí" d="Góc phải dưới · kéo bong bóng để đổi" />
      </Card>

      <Card id="look2" title="Khung làm việc">
        <Row k="Thu gọn menu trái" d="Chỉ còn thanh icon 56px · rê chuột để mở menu nổi"><Switch on={!!sw.side} label="Thu gọn menu trái" onChange={tog('side', 'thu gọn menu')} /></Row>
        <Row k="Ghim thanh trên / dưới" d="Không tự ẩn khi cuộn"><Switch on={!!sw.pin} label="Ghim thanh trên / dưới" onChange={tog('pin', 'ghim thanh')} /></Row>
        <Row k="Tasklist rộng" d="Cột phải 372px thay vì 320px ở màn 1920"><Switch on={!!sw.wideTl} label="Tasklist rộng" onChange={tog('wideTl', 'Tasklist rộng')} /></Row>
      </Card>

      <Card id="ask" title="Ask HANA" meta="Cách gợi ý · gợi ý chủ động · Ask HANA+">
        <Row k="Cách gợi ý" d="Tắt · chỉ khi tôi hỏi · Hana chủ động nhắc trên màn đang làm"><Seg label="Cách gợi ý" value={sg.sug!} onChange={pick('sug', 'Cách gợi ý')} items={[['off', 'Tắt'], ['ask', 'Khi tôi hỏi'], ['smart', 'Chủ động']]} /></Row>
        <Row k="H3 · Nhắc cut-off sát giờ" d="Job tôi phụ trách còn dưới 4 giờ làm việc tới CloseCY / CloseDoc"><Switch on={!!sw.h3} label="H3 · Nhắc cut-off sát giờ" onChange={tog('h3', 'gợi ý H3')} /></Row>
        <Row k="H4 · Gợi ý trả lời mail khách" d="Soạn sẵn bản nháp trong HANA Box, tôi duyệt rồi mới gửi"><Switch on={!!sw.h4} label="H4 · Gợi ý trả lời mail khách" onChange={tog('h4', 'gợi ý H4')} /></Row>
        <Row k="H6 · Phát hiện chứng từ lệch" d="So SI · Draft B/L · tờ khai, báo ô lệch"><Switch on={!!sw.h6} label="H6 · Phát hiện chứng từ lệch" onChange={tog('h6', 'gợi ý H6')} /></Row>
        <Row k="H7 · Tóm tắt đầu ngày" d="07:30 mỗi ngày làm việc · theo WorkCalendar"><Switch on={!!sw.h7} label="H7 · Tóm tắt đầu ngày" onChange={tog('h7', 'gợi ý H7')} /></Row>
        <Row k="H10 · Gợi ý follow khách im lặng" d="Khách không phản hồi báo giá sau 3 ngày làm việc"><Switch on={!!sw.h10} label="H10 · Gợi ý follow khách im lặng" onChange={tog('h10', 'gợi ý H10')} /></Row>
        <Row k="Ask HANA+ của tôi" warn={!!plusReq} d={plusReq ? `Đã xin thêm: ${plusReq} · chờ Admin IT duyệt` : '3 / 13 chức năng được cấp · cấp bởi Admin IT 01/Oct/2026'} />
        <Items>
          {HANA_PLUS.map(([key, k, on]) => (
            <Item key={key} k={k} sub={key} chip={on ? 'Hana+' : plusReq === k ? 'Đã xin' : 'Model chung'} tone={on ? 'pri' : plusReq === k ? 'warn' : 'mute'}>
              {!on && !plusReq && <SB soft label={`Xin cấp Ask HANA+ cho ${k}`} onClick={() => { setPlusReq(k); say(`Đã gửi xin Ask HANA+ cho ${k} · Admin IT duyệt`); }}>{t('sys.requestPlus')}</SB>}
            </Item>
          ))}
        </Items>
        <Row k="Lượt hỏi tháng 10" d="412 lượt · trong đó 38 lượt Hana+ · đọc 61 chứng từ"><b className={s.val}>{t('sys.turns412')}</b><Button onClick={() => { setRight(true); say('Lượt hỏi theo ngày ở cột phải › Thay đổi'); }}>{t('sys.viewDetail')}</Button></Row>
        <div className={s.note}>{t('sys.plusNote')}</div>
      </Card>

      <Card id="security" title="Bảo mật" meta="Passkey · tự khoá · tài khoản Microsoft">
        <Row k="Passkey" d={`${PASSKEYS.length - pkDel.length} khoá · đăng nhập và chấm công không cần mật khẩu`}><Button variant="soft" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => say('Đã tạo passkey MacBook · Touch ID')}>{t('sys.addPasskey')}</Button></Row>
        <Items>
          {PASSKEYS.map(([k, sub], i) => !pkDel.includes(i) && <Item key={k} k={k} sub={sub}><SB label={`Gỡ passkey ${k}`} onClick={() => { setPkDel((o) => [...o, i]); say(`Đã gỡ passkey ${k}`); }}>{t('sys.remove')}</SB></Item>)}
        </Items>
        <Row k="Tự khoá màn khi rảnh" d="Mở lại bằng passkey hoặc mật khẩu M365"><Seg label="Tự khoá" value={sg.lock!} onChange={pick('lock', 'Tự khoá')} items={[['0', 'Tắt'], ['5', '5 phút'], ['15', '15 phút'], ['30', '30 phút']]} /></Row>
        <Row k="Khoá ngay" d="Rời bàn làm việc · phím Ctrl + L"><Button onClick={() => say('Đã khoá màn · mở bằng Windows Hello')}>{t('sys.lockScreen')}</Button></Row>
        <Row k="Tài khoản Microsoft 365" d="gi•••@hanalogisticsvn.com · đổi mật khẩu, MFA, thiết bị"><a className={s.lnk} href="https://myaccount.microsoft.com" target="_blank" rel="noreferrer">{t('sys.openMs')}</a></Row>
      </Card>

      <Card id="desk" title="Bàn làm việc & ghế" meta="Trang chủ · dock · ghế đang làm · định dạng">
        <Row k="Ghế đang làm việc" d="Tasklist, Duyệt chờ tôi và trang chủ theo ghế này"><Seg label="Ghế đang làm việc" value={seat} onChange={(v) => { setSeat(v); say(`Đang làm việc với ghế ${v} · Tasklist, hộp duyệt đổi theo`); }} items={[['SALE-MGR', 'SALE-MGR'], ['BOD-DDIR', 'BOD-DDIR (quyền)']]} /></Row>
        <Row k="Bố cục trang chủ" d="Theo vai trò: thẻ xếp theo ghế đang làm"><Seg label="Bố cục" value={sg.desk!} onChange={pick('desk', 'Bố cục')} items={[['role', 'Theo vai trò'], ['compact', 'Gọn'], ['wide', 'Rộng']]} /></Row>
        <Row k="Thẻ trên trang chủ" d={hide.length ? `Đang ẩn: ${hide.join(' · ')}` : 'Hiện đủ 6 thẻ · bấm để ẩn / hiện'} />
        <Items>
          {DESK_CARDS.map((c) => { const h = hide.includes(c); return <Item key={c} k={c} sub={h ? 'đang ẩn' : 'đang hiện'} dim={h}><SB label={`${h ? 'Hiện' : 'Ẩn'} ${c}`} onClick={() => { setHide((o) => (h ? o.filter((x) => x !== c) : [...o, c])); say(`${h ? 'Đã hiện' : 'Đã ẩn'} thẻ ${c}`); }}>{h ? 'Hiện' : 'Ẩn'}</SB></Item>; })}
        </Items>
        <Row k="Vị trí dock" d="Thanh lối tắt nổi"><Seg label="Dock" value={sg.dock!} onChange={pick('dock', 'Dock')} items={[['left', 'Trái'], ['right', 'Phải'], ['bottom', 'Dưới']]} /></Row>
        <Row k="Mục trên dock" d="Sắp xếp bằng ↑ ↓" />
        <Items>
          {dock.map((k, i) => (
            <Item key={k} k={`${i + 1}. ${k}`}>
              <SB label={`Đưa ${k} lên`} onClick={() => { setDock((o) => moveItem(o, i, -1)); say(`Đã đổi thứ tự dock: ${k}`); }}>↑</SB>
              <SB label={`Đưa ${k} xuống`} onClick={() => { setDock((o) => moveItem(o, i, 1)); say(`Đã đổi thứ tự dock: ${k}`); }}>↓</SB>
            </Item>
          ))}
        </Items>
        <Row k="Ghim dock" d="Không tự ẩn"><Switch on={!!sw.dockPin} label="Ghim dock" onChange={tog('dockPin', 'ghim dock')} /></Row>
        <Row k="Định dạng số" d="Áp cho bảng, ô nhập và tệp xuất"><Seg label="Định dạng số" value={sg.num!} onChange={pick('num', 'Định dạng số')} items={[['vi', '1.234.567'], ['en', '1,234,567']]} /></Row>
        <Row k="Danh sách Job mặc định" d="Mở Job thì vào tab này"><Seg label="Job mặc định" value={sg.jr!} onChange={pick('jr', 'Job mặc định')} items={[['mine', 'Của tôi'], ['team', 'Team'], ['all', 'Tất cả']]} /></Row>
        <Row k="My Files · đặt tên tệp theo JobID" d="Tệp tải lên tự thêm tiền tố SE2606…"><Switch on={!!sw.files} label="My Files · đặt tên tệp theo JobID" onChange={tog('files', 'đặt tên theo JobID')} /><a className={s.lnk} href="/ezy/files">{t('sys.openFiles')}</a></Row>
      </Card>

      <Card id="tools" title="Tiện ích & góp ý" meta="Lối vào nhanh · góp ý của tôi">
        <Row k="Nhập số liệu hải quan" d="Customs Data: tải tệp tờ khai, tra HS"><a className={s.lnk} href="/ezy/customs">{t('sys.openCustoms')}</a></Row>
        <Row k="HANA Mee ↗" d="Chấm công, đơn, phiếu lương, giấy tờ"><a className={s.lnk} href="/mee">{t('sys.openMee')}</a></Row>
        <Row k="Góp ý của tôi" d="2 góp ý · trả lời báo qua HANA Hei">
          <Button variant="soft" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => say('Mở form góp ý · gửi tới Admin IT')}>{t('sys.sendFeedback')}</Button>
          <Button variant="soft" onClick={() => say('Xem trao đổi chưa mở ở bản này')}>{t('sys.viewThread')}</Button>
          <Button variant="soft" onClick={() => say('Góp ý giao cho tôi chưa mở ở bản này')}>{t('sys.assignedToMe')}</Button>
        </Row>
        <Items>
          <Item k="FB-0031 · Bảng giá chậm khi lọc theo khách" sub="Setting · Performance" chip="Resolved" tone="ok" />
          <Item k="FB-0032 · Thêm cột Incoterm vào danh sách Job" sub="Job Report · Feature Request" chip="New" tone="pri" />
        </Items>
      </Card>

      <Card id="noti" title="Thông báo theo kênh" label="Thông báo">
        <table className={s.tbl}>
          <thead><tr><th scope="col" className={s.thL}>{t('sys.type')}</th>{CHANNELS.map((c) => <th key={c} scope="col" className={s.thC}>{c}</th>)}</tr></thead>
          <tbody>
            {NOTI.map((n) => (
              <tr key={n.id}>
                <td className={s.tdL}><div className={s.rk}>{n.k}</div><div className={`${s.rd} ${n.locked ? s.warn : ''}`}>{n.sub}</div></td>
                {CHANNELS.map((c, ci) => {
                  const lock = n.locked && n.def[ci] === 1;
                  const on = notiOn(n, ci, noti);
                  const label = `${n.k} qua ${c}`;
                  return (
                    <td key={c} className={s.tdC}>
                      <button type="button" role="switch" aria-checked={on} aria-label={label} title={lock ? 'P0 luôn bật' : on ? 'Tắt' : 'Bật'} className={`${s.sw} ${s.swS} ${on ? s.swOn : ''} ${lock ? s.lock : ''}`}
                        onClick={() => { if (lock) { say('Khẩn P0 không tắt được trong app'); return; } setNoti((o) => ({ ...o, [`${n.id}${ci}`]: !on })); say(`${on ? 'Đã tắt' : 'Đã bật'} ${c} · ${n.k.split(' · ')[0]}`); }}><span /></button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card id="noti2" title="Thiết bị nhận push & hộp thư theo dõi" meta="Đăng ký theo máy · gửi thử">
        <Row k="Thiết bị nhận push" d={`${DEVICES.length - devOff.length} máy`}><Button variant="soft" icon={<IconPlus size={16} aria-hidden="true" />} onClick={() => say('Đã đăng ký push trên MacBook · Edge')}>{t('sys.regDevice')}</Button></Row>
        <Items>
          {DEVICES.map(([k, sub], i) => !devOff.includes(i) && (
            <Item key={k} k={k} sub={devTest.includes(i) ? 'đã gửi thử 23/Oct/2026 09:10 · máy xác nhận nhận được' : sub}>
              <SB label={`Gửi thử tới ${k}`} onClick={() => { setDevTest((o) => [...o, i]); say(`Đã gửi thử tới ${k}`); }}>{t('sys.sendTest')}</SB>
              <SB label={`Gỡ ${k}`} onClick={() => { setDevOff((o) => [...o, i]); say(`Đã gỡ ${k} khỏi push`); }}>{t('sys.remove')}</SB>
            </Item>
          ))}
        </Items>
        <Row k="Hộp thư chung theo dõi" d="Mail được gắn PIC cho tôi trong các hộp đang bật" />
        <Items>
          {MAILBOXES.map(([k, sub, n]) => { const on = !!mb[k]; return (
            <Item key={k} k={`${k} · ${n} mail tuần này`} sub={sub} chip={on ? 'Đang theo dõi' : 'Không theo dõi'} tone={on ? 'ok' : 'mute'}>
              <SB onClick={() => { setMb((o) => ({ ...o, [k]: !on })); say(`${on ? 'Đã bỏ theo dõi' : 'Đã theo dõi'} ${k}`); }}>{on ? 'Bỏ theo dõi' : 'Theo dõi'}</SB>
            </Item>
          ); })}
        </Items>
      </Card>

      <Card id="backup" title="Backup và uỷ quyền">
        <div>
          <b className={s.sub}>{t('sys.backupBySeat')}</b>
          {BACKUPS.map((b) => {
            const who = bk[b.seat] ?? b.def;
            const open = bkOpen === b.seat;
            return (
              <div key={b.seat} className={s.bk}>
                <b className={s.bkSeat}>{b.seat}</b><span className={s.bkScope}>{b.scope}</span>
                <button type="button" className={s.pop} aria-haspopup="listbox" aria-expanded={open} onClick={() => setBkOpen(open ? null : b.seat)}>{who} <span aria-hidden="true">▾</span></button>
                {open && (
                  <div role="listbox" aria-label="Chọn backup" className={s.list}>
                    {b.opts.map(([k, n]) => <button key={k} type="button" role="option" aria-selected={k === who} className={s.opt} onClick={() => { setBk((o) => ({ ...o, [b.seat]: k })); setBkOpen(null); say(`Backup ${b.seat} → ${k}`); }}><span>{k}</span><span className={s.rd}>{n}</span></button>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div>
          <div className={s.dHead}>
            <b className={s.sub}>{t('sys.delegActive')}</b>
            <div className={s.acts}>
              <Button variant="soft" onClick={() => say('Đã đánh dấu vắng hôm nay · việc chuyển cho backup')}>{t('sys.absentToday')}</Button>
              <Button variant="soft" onClick={() => setMDel(true)}>{t('sys.delegate')}</Button>
            </div>
          </div>
          {dels.length === 0 && <div className={s.empty}>{t('sys.noDeleg')}</div>}
          {dels.map((d) => (
            <div key={d.id} className={s.del}>
              <span className={`${s.chip} ${d.dir === 'Tôi giao' ? s.pri : s.warn}`}>{d.dir}</span>
              <div className={s.il}><div className={s.ik}>{d.k}</div><div className={s.rd}>{d.when}</div></div>
              <Button variant="ghost" onClick={() => { setDels((l) => l.filter((x) => x.id !== d.id)); say(`Đã kết thúc uỷ quyền ${d.k}`); }}>{t('sys.end')}</Button>
            </div>
          ))}
        </div>
      </Card>

      <Card id="sessions" title="Phiên đăng nhập và thiết bị">
        <div className={s.dHead}><b className={s.sub}>{t('sys.sessionsHd')}</b><Button variant="danger" onClick={() => setOutAll(true)}>{t('sys.signOutAll')}</Button></div>
        {sessions.map((x) => (
          <div key={x.k} className={s.ses}>
            <span aria-hidden="true" className={s.sIco}><IconDeviceDesktop size={18} /></span>
            <div className={s.il}><div className={s.ik}>{x.k}{x.cur && <span className={`${s.chip} ${s.ok} ${s.sm}`}>{t('sys.thisDevice')}</span>}{x.reg && <span className={`${s.chip} ${s.pri} ${s.sm}`}>{t('sys.clockReg')}</span>}</div><div className={s.rd}>{x.where}</div></div>
            {!x.cur && <Button onClick={() => setOut(x.k)}>{t('sys.signOut')}</Button>}
          </div>
        ))}
      </Card>

      <Card id="keys" title="Phím tắt">
        <div className={s.dHead}><span /><Button icon={<IconPrinter size={16} aria-hidden="true" />} onClick={() => say('In bảng phím tắt chưa mở ở bản này')}>{t('sys.printKeys')}</Button></div>
        <div className={s.keys}>
          {KEYS.map(([k, ks]) => <div key={k} className={s.key}><span>{k}</span><span className={s.kbds}>{ks.map((x) => <kbd key={x}>{x}</kbd>)}</span></div>)}
        </div>
      </Card>
    </>
  );

  return (
    <Page dock={wide ? <ActPanel tab={rTab} onTab={setRTab} /> : undefined}>
      <PageHeader
        title="Cài đặt"
        actions={
          <>
            <HeaderTool label="Thông báo, 4 chưa đọc" icon={IconBell} n={4} onClick={() => say('Thông báo chưa mở ở bản này')} />
            {!wide && <HeaderTool label="Hoạt động tài khoản" icon={IconActivity} active={right} onClick={() => setRight((o) => !o)} />}
            <Button onClick={() => say('Quản trị hệ thống mở ở bước sau')}>{state === 'noperm' && <IconLock size={14} aria-hidden="true" />}Quản trị hệ thống</Button>
          </>
        }
      />
      <div className={s.split}>
        <nav className={s.nav} aria-label="Mục cài đặt">
          {SECS.map(([id, k]) => {
            const Ico = SEC_ICON[id];
            return <button key={id} type="button" className={s.navB} aria-current={cur === id ? 'true' : undefined} onClick={() => go(id)}><Ico size={16} stroke={1.9} aria-hidden="true" />{k}{SEC_N[id] && <span className={s.navN}>{SEC_N[id]}</span>}</button>;
          })}
        </nav>
        <div className={s.content}>
          <StateView state={state === 'empty' || state === 'noperm' ? 'ready' : state} title={state === 'error' ? 'Không tải được cài đặt của anh' : undefined} action={state === 'error' ? <Button onClick={() => location.reload()}>{t('sys.retry')}</Button> : undefined} skeleton={<Skeleton rows={8} />}>
            {ready}
          </StateView>
        </div>
        {!wide && right && <aside className={s.actFloat} aria-label="Hoạt động tài khoản"><ActPanel tab={rTab} onTab={setRTab} /></aside>}
      </div>

      <Modal open={mDel} onOpenChange={(o) => { if (!o) closeDel(); }} title="Uỷ quyền tạm" footer={<><Button variant="ghost" onClick={closeDel}>{t('sys.cancel')}</Button><Button variant="primary" onClick={saveDel}>{t('sys.delegate')}</Button></>}>
        <div className={s.form}>
          <label>{t('sys.seat')}<select className={s.sel} value={dSeat} onChange={(e) => setDSeat(e.target.value)}><option value="SALE-MGR">{seatLine('SALE-MGR')}</option><option value="BOD-DDIR">{seatLine('BOD-DDIR')}</option></select></label>
          <label>{t('sys.recipient')}<select className={s.sel} aria-invalid={dTried && !dTo} value={dTo} onChange={(e) => setDTo(e.target.value)}><option value="">{t('sys.pickPerson')}</option>{PEOPLE.map(([c, r]) => <option key={c} value={c}>{c} · {r}</option>)}</select></label>
          {dTried && !dTo && <div className={s.errT}>{t('sys.pickRecipient')}</div>}
        </div>
      </Modal>
      <Modal danger open={out !== null || outAll} onOpenChange={(o) => { if (!o) { setOut(null); setOutAll(false); } }} title={outAll ? 'Đăng xuất mọi máy khác?' : `Đăng xuất ${out ?? ''}?`} description="Máy đó phải đăng nhập lại bằng Microsoft 365. Thiết bị chấm công đăng ký lại cần HR duyệt."
        footer={<><Button onClick={() => { setOut(null); setOutAll(false); }}>{t('sys.keep')}</Button><Button variant="danger" onClick={() => { setGone((g) => (outAll ? SESSIONS.filter((x) => !x.cur).map((x) => x.k) : out ? [...g, out] : g)); say(outAll ? 'Đã đăng xuất mọi máy khác' : `Đã đăng xuất ${out}`); setOut(null); setOutAll(false); }}>{t('sys.signOut')}</Button></>}>
        <span />
      </Modal>
    </Page>
  );
}
